
import React, { useState, useEffect, useRef, useMemo, useImperativeHandle, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button } from '@mui/material';
import { useAuth } from '../../auth/Auth';

/** Walk inline nodes; Quill treats <br> as a newline within a block. */
const inlineQuillText = (node) => {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent || '';
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return '';
  }
  if (node.tagName === 'BR') {
    return '\n';
  }
  let text = '';
  node.childNodes.forEach((child) => {
    text += inlineQuillText(child);
  });
  return text;
};

/** Convert stored HTML to plain text the same way Quill getText() does (block breaks → \\n). */
export const htmlToQuillPlainText = (html) => {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = String(html);

  const blockTags = new Set(['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'BLOCKQUOTE', 'PRE', 'DIV']);

  const parts = [];
  div.childNodes.forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE && blockTags.has(node.tagName)) {
      parts.push(inlineQuillText(node).replace(/\n$/, ''));
    } else if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      if (text) parts.push(text);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      parts.push(inlineQuillText(node).replace(/\n$/, ''));
    }
  });

  const combined = parts.length > 0 ? parts.join('\n') : inlineQuillText(div);
  return combined.replace(/\n$/, '');
};

/** Plain-text length matching Quill getText() (excludes trailing newline). */
export const getQuillPlainLength = (htmlOrEditor) => {
  if (!htmlOrEditor) return 0;
  if (typeof htmlOrEditor === 'object' && typeof htmlOrEditor.getText === 'function') {
    return Math.max(0, htmlOrEditor.getText().replace(/\n$/, '').length);
  }
  return htmlToQuillPlainText(String(htmlOrEditor)).length;
};

/** Escape plain text into a single Quill-friendly paragraph. */
const plainToHtml = (plain) => {
  const escaped = String(plain || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br/>');
  return `<p>${escaped || '<br>'}</p>`;
};

const RichTextEditor = React.forwardRef(({
  value,
  onChange,
  readOnly = false,
  height = 300,
  showAiGenerate = true,
  resizable = false,
  maxLength,
  onMaxLengthExceeded,
  onPlainLengthChange,
}, ref) => {
  const quillRef = useRef(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [context, setContext] = useState('');
  const { axiosInstance } = useAuth();

  const handleAIGenerate = useCallback(() => {
    setOpenDialog(true);
  }, []);

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setContext('');
  };

  const handleGenerateEmail = async () => {
    try {
      const response = await axiosInstance.post('/generate/email', { context });
      const generatedEmail = response.data.email;
      onChange(generatedEmail);
      handleCloseDialog();
    } catch (error) {
      console.error('Error generating email:', error);
    }
  };

  const reportPlainLength = useCallback((lengthOverride) => {
    if (lengthOverride != null) {
      onPlainLengthChange?.(lengthOverride);
      return;
    }
    const quill = quillRef.current?.getEditor?.();
    // Always prefer live Quill length — HTML parsing can under-count (<br>, blocks).
    onPlainLengthChange?.(getQuillPlainLength(quill || value));
  }, [onPlainLengthChange, value]);

  const trimToMaxLength = useCallback((content, unprivileged) => {
    const quill = quillRef.current?.getEditor?.();
    const plainLen = quill
      ? getQuillPlainLength(quill)
      : getQuillPlainLength(unprivileged || content);

    if (maxLength == null || plainLen <= maxLength) {
      return { html: content, truncated: false, length: plainLen };
    }

    // Prefer real Quill instance (has deleteText). ReactQuill's onChange "editor"
    // arg is an unprivileged proxy and must NOT call deleteText on it.
    if (quill && typeof quill.deleteText === 'function') {
      try {
        const excess = plainLen - maxLength;
        if (excess > 0) {
          const deleteIndex = quill.getLength() - 1 - excess;
          quill.deleteText(Math.max(0, deleteIndex), excess, 'silent');
        }
        return {
          html: quill.root?.innerHTML ?? content,
          truncated: true,
          length: getQuillPlainLength(quill),
        };
      } catch (err) {
        console.warn('Quill truncate failed, using plain-text fallback', err);
      }
    }

    const plain = htmlToQuillPlainText(String(content)).slice(0, maxLength);
    return { html: plainToHtml(plain), truncated: true, length: plain.length };
  }, [maxLength]);

  const handleChange = useCallback((content, _delta, source, unprivileged) => {
    // ReactQuill re-pastes `value` on prop changes (source 'api') and Quill may
    // normalize the HTML (e.g. drop a trailing <p><br></p>). Echoing that back to
    // the parent — through ReactQuill's stale props — can ping-pong between
    // sibling editors forever. Only user edits change the stored value.
    if (source !== 'user') {
      reportPlainLength();
      return;
    }
    if (maxLength == null || readOnly) {
      onChange?.(content);
      reportPlainLength();
      return;
    }

    const { html, truncated, length } = trimToMaxLength(content, unprivileged);
    if (truncated) {
      onMaxLengthExceeded?.(maxLength);
    }
    onChange?.(html);
    reportPlainLength(length);
  }, [maxLength, onChange, onMaxLengthExceeded, readOnly, trimToMaxLength, reportPlainLength]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const quill = quillRef.current?.getEditor?.();
      if (quill) {
        onPlainLengthChange?.(getQuillPlainLength(quill));
      } else {
        reportPlainLength();
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [value, reportPlainLength, onPlainLengthChange]);

  const modules = useMemo(() => ({
    toolbar: readOnly ? false : {
      container: [
        [{ 'header': [1, 2, false] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
        ['link', 'image'],
        ['clean']
      ]
    },
  }), [readOnly]);

  const formats = useMemo(() => [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'image'
  ], []);

  useEffect(() => {
    if (!showAiGenerate || readOnly || !quillRef.current) return;

    const toolbar = quillRef.current.getEditor().getModule('toolbar');
    toolbar.addHandler('ai-generate', handleAIGenerate);

    const aiButton = document.createElement('button');
    aiButton.setAttribute('type', 'button');
    aiButton.setAttribute('title', 'Generate with AI');
    aiButton.className = 'ql-ai-generate';
    aiButton.innerHTML = `<svg viewBox="0 0 50 50" style="width: 18px; height: 18px;">
        <path d="M 13.332031 4 C 8.1895418 4 4 8.1895418 4 13.332031 L 4 36.667969 C 4 41.810458 8.1895418 46 13.332031 46 L 36.667969 46 C 41.810458 46 46 41.809388 46 36.666016 L 46 13.332031 C 46 8.1895418 41.809388 4 36.666016 4 L 13.332031 4 z M 13.332031 6 L 36.666016 6 C 40.728643 6 44 9.2705212 44 13.332031 L 44 36.666016 C 44 40.728643 40.729479 44 36.667969 44 L 13.332031 44 C 9.2705211 44 6 40.729479 6 36.667969 L 6 13.332031 C 6 9.2705211 9.2705212 6 13.332031 6 z M 15.398438 11.072266 C 14.745876 11.072266 14.03981 11.510017 13.804688 12.224609 C 13.433639 13.352756 12.550427 14.237411 11.423828 14.607422 C 10.709588 14.842104 10.271484 15.547138 10.271484 16.199219 C 10.271484 16.851779 10.709236 17.555892 11.423828 17.791016 C 12.551329 18.162112 13.433681 19.045813 13.804688 20.173828 C 14.03966 20.88796 14.745824 21.326172 15.398438 21.326172 C 16.051049 21.326172 16.755262 20.88796 16.990234 20.173828 C 17.361331 19.046327 18.243079 18.162021 19.371094 17.791016 C 20.085225 17.556043 20.523436 16.851831 20.523438 16.199219 C 20.523438 15.546607 20.085225 14.842394 19.371094 14.607422 C 18.244135 14.236504 17.361554 13.3538 16.990234 12.226562 L 16.990234 12.224609 C 16.755552 11.510369 16.050517 11.072266 15.398438 11.072266 z M 30.642578 12.955078 C 29.939863 12.955078 29.183764 13.421678 28.929688 14.193359 L 27.798828 17.634766 C 27.340782 19.027818 26.248029 20.119046 24.853516 20.578125 L 21.414062 21.710938 C 20.64227 21.964706 20.173828 22.71864 20.173828 23.421875 C 20.173828 24.12511 20.64227 24.880997 21.414062 25.134766 L 24.855469 26.265625 C 26.248521 26.723671 27.338758 27.814893 27.796875 29.208984 L 28.929688 32.650391 C 29.183456 33.422183 29.93739 33.890625 30.640625 33.890625 C 31.34386 33.890625 32.099747 33.422183 32.353516 32.650391 L 33.484375 29.208984 C 33.942421 27.815932 35.034259 26.724633 36.427734 26.265625 L 39.869141 25.134766 C 40.640933 24.880997 41.107422 24.12511 41.107422 23.421875 C 41.107422 22.71864 40.640933 21.964706 39.869141 21.710938 L 36.427734 20.578125 C 35.035223 20.120257 33.942735 19.028995 33.484375 17.636719 L 33.484375 17.634766 L 32.353516 14.193359 C 32.099438 13.421678 31.345293 12.955078 30.642578 12.955078 z M 15.398438 13.349609 C 15.973612 14.629092 16.967378 15.624199 18.246094 16.199219 C 16.966223 16.77435 15.973557 17.769805 15.398438 19.048828 C 14.823163 17.768641 13.828293 16.774257 12.548828 16.199219 C 13.828481 15.624423 14.823392 14.629739 15.398438 13.349609 z M 30.640625 15.390625 L 31.585938 18.259766 C 32.24189 20.254713 33.807787 21.822562 35.802734 22.478516 L 38.673828 23.421875 L 35.802734 24.365234 C 33.80821 25.022227 32.241891 26.589037 31.585938 28.583984 L 30.640625 31.455078 L 29.697266 28.583984 C 29.041561 26.588617 27.474643 25.021504 25.480469 24.365234 L 25.478516 24.365234 L 22.609375 23.421875 L 25.480469 22.478516 C 27.475955 21.821594 29.041312 20.254713 29.697266 18.259766 L 30.640625 15.390625 z M 18.607422 27.396484 C 17.904707 27.396484 17.148609 27.863084 16.894531 28.634766 L 16.755859 29.056641 L 16.755859 29.058594 C 16.297813 30.451646 15.205975 31.542945 13.8125 32.001953 L 13.390625 32.140625 C 12.619775 32.394905 12.152344 33.148809 12.152344 33.851562 C 12.152344 34.554318 12.619775 35.310174 13.390625 35.564453 L 13.8125 35.703125 L 13.814453 35.703125 C 15.207506 36.161171 16.297813 37.251479 16.755859 38.644531 L 16.755859 38.646484 L 16.894531 39.068359 C 17.148811 39.83921 17.904667 40.306641 18.607422 40.306641 C 19.310657 40.306641 20.064591 39.838199 20.318359 39.066406 L 20.457031 38.646484 L 20.457031 38.644531 C 20.915077 37.251479 22.006299 36.161242 23.400391 35.703125 L 23.402344 35.703125 L 23.824219 35.564453 C 24.596012 35.310684 25.0625 34.554798 25.0625 33.851562 C 25.0625 33.148328 24.596012 32.394394 23.824219 32.140625 L 23.402344 32.001953 L 23.400391 32.001953 C 22.007338 31.543907 20.91611 30.451154 20.457031 29.056641 L 20.318359 28.634766 C 20.064282 27.863084 19.310137 27.396484 18.607422 27.396484 z M 18.607422 29.761719 C 19.270822 31.684108 20.775622 33.188583 22.697266 33.851562 C 20.77522 34.514274 19.270225 36.020278 18.607422 37.941406 C 17.944636 36.020284 16.4387 34.514348 14.517578 33.851562 C 16.439044 33.188088 17.944459 31.683357 18.607422 29.761719 z" />
      </svg>`;

    const toolbarElement = toolbar.container;
    toolbarElement.appendChild(aiButton);

    return () => {
      if (toolbarElement && aiButton.parentNode === toolbarElement) {
        toolbarElement.removeChild(aiButton);
      }
    };
  }, [readOnly, showAiGenerate, handleAIGenerate]);

  useImperativeHandle(ref, () => ({
    getEditor: () => quillRef.current?.getEditor(),
  }), []);

  const editorBodyHeight = Math.max(80, height - 42);

  return (
    <>
      <Box sx={{ 
        '& .quill': { 
          height: `${height}px`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          ...(resizable && {
            minHeight: `${height}px`,
            resize: 'vertical',
          }),
        },
        '& .ql-toolbar': {
          flexShrink: 0,
        },
        // Single scroll surface on the editor only (avoids nested scrollbars
        // from both .ql-container and .ql-editor).
        '& .ql-container': {
          flex: '1 1 auto',
          height: 'auto',
          overflow: 'hidden',
          fontSize: 'inherit',
        },
        '& .ql-editor': {
          height: `${editorBodyHeight}px`,
          minHeight: `${editorBodyHeight}px`,
          maxHeight: `${editorBodyHeight}px`,
          overflowY: 'auto',
          overflowX: 'hidden',
        }
      }}>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={handleChange}
          modules={modules}
          formats={formats}
          readOnly={readOnly}
        />
      </Box>
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Generate Email with AI</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Context for email generation"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={context}
            onChange={(e) => setContext(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleGenerateEmail} variant="contained" color="primary">
            Generate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
